import React from "react";
import PropTypes from "prop-types";
import Icon from "./icon";
import Theme from "../../resources/themes/light.js";
import "./Button.css";

const cn = (...values) => values.filter(Boolean).join(" ");

const variantClassMap = {
  primary: "custom-button--primary",
  yellow: "custom-button--yellow",
  secondary: "custom-button--secondary",
  destructive: "custom-button--destructive",
  outline: "custom-button--outline",
  ghost: "custom-button--ghost",
  link: "custom-button--link",
};

const sizeClassMap = {
  default: "custom-button--default",
  xs: "custom-button--xs",
  sm: "custom-button--sm",
  lg: "custom-button--lg",
  xl: "custom-button--xl",
  icon: "custom-button--icon",
};

/**
 * Custom Button Component
 * 
 * @param {Object} props
 * @param {React.ReactNode} [props.children] - Button content
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.icon] - Icon name to display
 * @param {"left" | "right"} [props.iconPosition="left"] - Position of the icon
 * @param {string|number} [props.iconWidth=16] - Custom icon width
 * @param {string|number} [props.iconHeight=16] - Custom icon height
 * @param {string} [props.iconClassName] - CSS class for the icon
 * @param {Object} [props.iconProps] - Additional props for the Icon component
 * @param {"primary" | "yellow" | "secondary" | "destructive" | "outline" | "ghost" | "link"} [props.variant="primary"] - Button style variant
 * @param {"default" | "xs" | "sm" | "lg" | "xl" | "icon"} [props.size="default"] - Button size
 * @param {boolean} [props.disabled=false] - Whether the button is disabled
 * @param {boolean} [props.isLoading=false] - Whether the button is in a loading state
 * @param {string|number} [props.fontSize] - Custom font size
 * @param {string|number} [props.fontWeight] - Custom font weight
 * @param {string|number} [props.lineHeight] - Custom line height
 * @param {string} [props.fontFamily] - Custom font family
 * @param {string|number} [props.padding] - Custom padding
 * @param {string|number} [props.paddingInline] - Custom inline padding
 * @param {string|number} [props.paddingBlock] - Custom block padding
 * @param {string|number} [props.width] - Custom width
 * @param {string|number} [props.minWidth] - Custom minimum width
 * @param {string|number} [props.maxWidth] - Custom maximum width
 * @param {string|number} [props.height="30px"] - Custom height
 * @param {string|number} [props.minHeight] - Custom minimum height
 * @param {string|number} [props.borderRadius] - Custom border radius
 * @param {string|number} [props.borderWidth] - Custom border width
 * @param {string} [props.borderColor] - Custom border color
 * @param {string} [props.backgroundColor] - Custom background color
 * @param {string} [props.textColor] - Custom text color
 * @param {Object} [props.style] - Inline styles
 * @param {"button" | "submit" | "reset"} [props.type="button"] - Button type
 */
const Button = React.forwardRef(
  (
    {
      children,
      className,
      icon,
      iconPosition = "left",
      iconWidth = 16,
      iconHeight = 16,
      iconClassName,
      iconProps,
      variant = "primary",
      size = "default",
      disabled = false,
      isLoading = false,
      fontSize,
      lineHeight,
      fontWeight,
      fontFamily,
      padding,
      paddingInline,
      paddingBlock,
      width,
      minWidth,
      maxWidth,
      height = "30px",
      minHeight,
      borderRadius,
      borderWidth,
      borderColor,
      backgroundColor,
      textColor,
      style,
      type = "button",
      ...props
    },
    ref
  ) => {
    const defaultTypography =
      Theme.typography?.influencerButton ||
      Theme.typography?.textButton ||
      {};
    const isButtonDisabled = disabled || isLoading;
    const resolvedFontSize = fontSize ?? defaultTypography.fontSize;
    const resolvedLineHeight = lineHeight ?? defaultTypography.lineHeight;
    const resolvedFontWeight = fontWeight ?? defaultTypography.fontWeight;
    const resolvedFontFamily = fontFamily ?? defaultTypography.fontFamily;

    const buttonStyle = {
      fontSize: resolvedFontSize,
      lineHeight: resolvedLineHeight,
      fontWeight: resolvedFontWeight,
      fontFamily: resolvedFontFamily,
      padding,
      paddingInline,
      paddingBlock,
      width,
      minWidth,
      maxWidth,
      height,
      minHeight,
      borderRadius,
      borderWidth,
      borderColor,
      background: backgroundColor,
      color: textColor,
      ...style,
    };

    return (
      <button
        ref={ref}
        type={type}
        className={cn(
          "custom-button",
          variantClassMap[variant] || variantClassMap.primary,
          sizeClassMap[size] || sizeClassMap.default,
          isLoading && "custom-button--loading",
          isButtonDisabled && "custom-button--disabled",
          className
        )}
        disabled={isButtonDisabled}
        aria-busy={isLoading}
        style={buttonStyle}
        {...props}
      >
        <span
          className="custom-button__content"
        >
          {isLoading ? (
            <span className="custom-button__spinner" aria-hidden="true" />
          ) : null}
          {icon && iconPosition === "left" && !isLoading ? (
            <span className="custom-button__icon" aria-hidden="true">
              <Icon
                iconName={icon}
                width={iconWidth}
                height={iconHeight}
                className={iconClassName}
                {...iconProps}
              />
            </span>
          ) : null}
          <span>{children}</span>
          {icon && iconPosition === "right" && !isLoading ? (
            <span className="custom-button__icon" aria-hidden="true">
              <Icon
                iconName={icon}
                width={iconWidth}
                height={iconHeight}
                className={iconClassName}
                {...iconProps}
              />
            </span>
          ) : null}
        </span>
      </button>
    );
  }
);

Button.displayName = "Button";

Button.propTypes = {
  children: PropTypes.node,
  className: PropTypes.string,
  icon: PropTypes.string,
  iconPosition: PropTypes.oneOf(["left", "right"]),
  iconWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  iconHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  iconClassName: PropTypes.string,
  iconProps: PropTypes.object,
  variant: PropTypes.oneOf(["primary", "yellow", "secondary", "destructive", "outline", "ghost", "link"]),
  size: PropTypes.oneOf(["default", "xs", "sm", "lg", "xl", "icon"]),
  disabled: PropTypes.bool,
  isLoading: PropTypes.bool,
  fontSize: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fontWeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  lineHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  fontFamily: PropTypes.string,
  padding: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  paddingInline: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  paddingBlock: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  maxWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  minHeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  borderRadius: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  borderWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  borderColor: PropTypes.string,
  backgroundColor: PropTypes.string,
  textColor: PropTypes.string,
  style: PropTypes.object,
  type: PropTypes.oneOf(["button", "submit", "reset"]),
};

export default Button;
